import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { companyService } from '../services/companyService';
import { watchlistService } from '../services/watchlistService';
import { userCompaniesService } from '../services/userCompaniesService';
import { dashboardService } from '../services/dashboardService';
import { companiesFromWatchlistItems } from '../utils/watchlistReadModelAdapter';
import { computeSupplierTierCounts } from '../utils/supplierTierCounts';
import { createCompany } from '../models/companyModels';
import { clearEtagEntry, etagKeyForUser } from '../services/etagStore';
import { tenantCompanyService } from '../services/tenantCompanyService';
import { getGraphContextCache } from '../services/graphContextCache';
import { hasMapNodesInCache, normalizeMapNodesByKey } from '../utils/tenantGraphContextAdapter';
import { TENANT_MAP_NODE_KEYS } from '../config/tenantNodeTabsConfig';

const WATCHLIST_ETAG_RESOURCE = 'watchlist-read-model';

const AppDataContext = createContext();

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

const DEFAULT_COMPANY_STATS = {
  totalCompanies: 0,
  companiesWithRiskAnalysis: 0,
};

const DEFAULT_WATCHLIST_STATS = {
  totalCompanies: 0,
  alertsEnabledCount: 0,
  priorityDistribution: { high: 0, normal: 0, low: 0 },
};

export const AppDataProvider = ({ children }) => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companiesStats, setCompaniesStats] = useState(DEFAULT_COMPANY_STATS);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistStats, setWatchlistStats] = useState(DEFAULT_WATCHLIST_STATS);
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [userCompanies, setUserCompanies] = useState([]);
  const [userCompaniesLoaded, setUserCompaniesLoaded] = useState(false);
  const [dashboardBootstrap, setDashboardBootstrap] = useState(null);
  const [tenantProfile, setTenantProfile] = useState(null);
  const [tenantNodesByKey, setTenantNodesByKey] = useState({});
  const [tenantSuppliers, setTenantSuppliers] = useState([]);
  const [tenantCompanyRoutes, setTenantCompanyRoutes] = useState({});
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantDetailsLoading, setTenantDetailsLoading] = useState(false);
  const [tenantLoaded, setTenantLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const watchlistRefreshPromiseRef = useRef(null);
  const tenantRefreshPromiseRef = useRef(null);
  const coreRefreshPromiseRef = useRef(null);
  const watchlistSyncTokenRef = useRef('');
  const refreshFnsRef = useRef({
    refreshDashboardBootstrap: null,
    refreshWatchlist: null,
    refreshUserCompanies: null,
  });

  const tenantMapReady = useMemo(
    () => TENANT_MAP_NODE_KEYS.some((key) => (tenantNodesByKey[key]?.length ?? 0) > 0),
    [tenantNodesByKey]
  );

  const supplierTierCounts = useMemo(
    () => computeSupplierTierCounts(userCompanies),
    [userCompanies]
  );

  const applyWatchlistStatistics = useCallback((stats) => {
    if (!stats) {
      setWatchlistStats(DEFAULT_WATCHLIST_STATS);
      return;
    }
    setWatchlistStats({
      totalCompanies: stats.totalCompanies ?? 0,
      alertsEnabledCount: stats.alertsEnabledCount ?? 0,
      priorityDistribution: stats.priorityDistribution ?? { high: 0, normal: 0, low: 0 },
    });
  }, []);

  const refreshDashboardBootstrap = useCallback(async () => {
    if (!user?.uid) return { success: false };
    try {
      const response = await dashboardService.getBootstrap(user.uid);
      if (!response.success || !response.data) {
        return response;
      }

      setDashboardBootstrap(response.data);

      const companyStats = response.data.companyStatistics;
      if (companyStats) {
        setCompaniesStats({
          totalCompanies: companyStats.totalCompanies ?? 0,
          companiesWithRiskAnalysis: companyStats.companiesWithRiskAnalysis ?? 0,
        });
      }

      if (response.data.watchlistStatistics) {
        applyWatchlistStatistics(response.data.watchlistStatistics);
      }

      const suppliers = response.data.watchlistGraph?.suppliers;
      if (Array.isArray(suppliers) && suppliers.length > 0) {
        setCompanies((prev) => {
          if (prev.length > 0) return prev;
          return suppliers.map((item) => createCompany(item));
        });
      }

      return { success: true, data: response.data };
    } catch (err) {
      return { success: false };
    }
  }, [user?.uid, applyWatchlistStatistics]);

  const refreshCompanies = useCallback(async () => {
    if (!user?.uid) return { success: false };
    try {
      const bootstrapRes = await refreshDashboardBootstrap();
      if (bootstrapRes.success) {
        return { success: true };
      }

      const statsRes = await companyService.getCompanyStatistics();
      if (statsRes.success && statsRes.data) {
        const d = statsRes.data;
        setCompaniesStats({
          totalCompanies: d.totalCompanies ?? 0,
          companiesWithRiskAnalysis: d.companiesWithRiskAnalysis ?? 0,
        });
      }
      return { success: true };
    } catch (err) {
      return { success: false };
    }
  }, [user?.uid, refreshDashboardBootstrap]);

  const refreshWatchlist = useCallback(async (options = {}) => {
    if (!user?.uid) return { success: false };
    if (watchlistRefreshPromiseRef.current) {
      return watchlistRefreshPromiseRef.current;
    }

    const refreshPromise = (async () => {
      setWatchlistLoading(true);
      try {
        if (options.force) {
          clearEtagEntry(etagKeyForUser(WATCHLIST_ETAG_RESOURCE, user.uid));
          watchlistSyncTokenRef.current = '';
        }

        const readModelRes = await watchlistService.getWatchlistReadModel(user.uid, {
          includeInboundRoutes: true,
          updatedSince: options.force ? undefined : watchlistSyncTokenRef.current || undefined,
          view: 'summary',
        });

        if (readModelRes.notModified) {
          return { success: true, notModified: true };
        }

        if (readModelRes.success && readModelRes.data) {
          const { items, statistics, syncToken } = readModelRes.data;
          if (Array.isArray(items)) {
            setWatchlist(items);
            setCompanies(companiesFromWatchlistItems(items));
          } else {
            setWatchlist([]);
          }
          applyWatchlistStatistics(statistics);
          if (syncToken) {
            watchlistSyncTokenRef.current = syncToken;
          }
        } else {
          setWatchlist([]);
          applyWatchlistStatistics(null);
        }

        return { success: Boolean(readModelRes.success) };
      } catch (err) {
        return { success: false };
      } finally {
        setWatchlistLoading(false);
      }
    })();

    watchlistRefreshPromiseRef.current = refreshPromise;
    try {
      return await refreshPromise;
    } finally {
      watchlistRefreshPromiseRef.current = null;
    }
  }, [user?.uid, applyWatchlistStatistics]);

  const hydrateTenantFromCache = useCallback((uid) => {
    const cached = getGraphContextCache(uid);
    if (!cached?.nodesByKey) return false;
    setTenantNodesByKey(normalizeMapNodesByKey(cached.nodesByKey));
    setTenantCompanyRoutes(cached.companyRoutes ?? {});
    return true;
  }, []);

  const applyTenantGraphData = useCallback((data) => {
    if (!data) return;
    if (data.nodesByKey) {
      setTenantNodesByKey((prev) => ({
        ...prev,
        ...normalizeMapNodesByKey(data.nodesByKey),
      }));
    }
    if (data.companyRoutes != null) {
      setTenantCompanyRoutes(data.companyRoutes);
    }
  }, []);

  const patchTenantNodesByKey = useCallback((key, rows) => {
    setTenantNodesByKey((prev) => ({
      ...prev,
      [key]: Array.isArray(rows) ? rows : [],
    }));
  }, []);

  const refreshTenantCompany = useCallback(async (options = {}) => {
    if (!user?.uid) {
      setTenantProfile(null);
      setTenantNodesByKey({});
      setTenantSuppliers([]);
      setTenantCompanyRoutes({});
      setTenantLoaded(false);
      return { success: false };
    }

    if (tenantRefreshPromiseRef.current) {
      return tenantRefreshPromiseRef.current;
    }

    const refreshPromise = (async () => {
      setTenantLoading(true);
      try {
        if (!options.skipCacheHydrate) {
          hydrateTenantFromCache(user.uid);
        } else if (hasMapNodesInCache(getGraphContextCache(user.uid))) {
          setTenantLoaded(true);
          setTenantLoading(false);
        }

        const graphRes = await tenantCompanyService.getGraphContext(user.uid, {
          includeWatchlistSuppliers: false,
        });

        if (graphRes.notModified && graphRes.data) {
          applyTenantGraphData(graphRes.data);
        } else if (graphRes.success && graphRes.data) {
          applyTenantGraphData(graphRes.data);
        } else {
          setTenantNodesByKey((prev) => normalizeMapNodesByKey(prev));
        }

        setTenantLoaded(true);
        setTenantLoading(false);

        setTenantDetailsLoading(true);
        const [profileRes, suppliersRes] = await Promise.all([
          tenantCompanyService.getTenantCompany(user.uid),
          tenantCompanyService.getTenantSuppliers(user.uid),
        ]);

        if (profileRes.success) {
          setTenantProfile(profileRes.data ?? null);
        }

        if (suppliersRes.success) {
          setTenantSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : []);
        }

        return { success: true };
      } catch (err) {
        return { success: false };
      } finally {
        setTenantLoading(false);
        setTenantDetailsLoading(false);
      }
    })();

    tenantRefreshPromiseRef.current = refreshPromise;
    try {
      return await refreshPromise;
    } finally {
      tenantRefreshPromiseRef.current = null;
    }
  }, [user?.uid, hydrateTenantFromCache, applyTenantGraphData]);

  const refreshUserCompanies = useCallback(async () => {
    if (!user?.uid) {
      setUserCompanies([]);
      setUserCompaniesLoaded(false);
      return { success: false };
    }
    try {
      const res = await userCompaniesService.listUserCompanies(user.uid);
      if (res.success && Array.isArray(res.data)) {
        setUserCompanies(res.data);
      } else {
        setUserCompanies([]);
      }
      return res;
    } catch (err) {
      setUserCompanies([]);
      return { success: false };
    } finally {
      setUserCompaniesLoaded(true);
    }
  }, [user?.uid]);

  const refreshCoreAppData = useCallback(async () => {
    if (coreRefreshPromiseRef.current) {
      return coreRefreshPromiseRef.current;
    }

    const refreshPromise = (async () => {
      const fns = refreshFnsRef.current;
      try {
        await Promise.all([
          fns.refreshDashboardBootstrap(),
          fns.refreshWatchlist({ force: true }),
          fns.refreshUserCompanies(),
        ]);
        return { success: true };
      } catch (err) {
        throw err;
      }
    })();

    coreRefreshPromiseRef.current = refreshPromise;
    try {
      return await refreshPromise;
    } finally {
      coreRefreshPromiseRef.current = null;
    }
  }, []);

  const refreshAll = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await refreshTenantCompany({ skipCacheHydrate: false });
      setLoading(false);
      await refreshCoreAppData();
    } catch (err) {
      setError(err.message || 'Failed to load app data');
    } finally {
      setLoading(false);
    }
  }, [user?.uid, refreshTenantCompany, refreshCoreAppData]);

  refreshFnsRef.current = {
    refreshDashboardBootstrap,
    refreshWatchlist,
    refreshUserCompanies,
    refreshTenantCompany,
  };

  useEffect(() => {
    if (!user?.uid) {
      setCompanies([]);
      setCompaniesStats(DEFAULT_COMPANY_STATS);
      setWatchlist([]);
      setWatchlistStats(DEFAULT_WATCHLIST_STATS);
      setWatchlistLoading(false);
      setUserCompanies([]);
      setUserCompaniesLoaded(false);
      setDashboardBootstrap(null);
      setTenantProfile(null);
      setTenantNodesByKey({});
      setTenantSuppliers([]);
      setTenantCompanyRoutes({});
      setTenantLoaded(false);
      setTenantDetailsLoading(false);
      watchlistSyncTokenRef.current = '';
      setLoading(false);
      return;
    }

    hydrateTenantFromCache(user.uid);
    if (hasMapNodesInCache(getGraphContextCache(user.uid))) {
      setTenantLoaded(true);
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        await refreshTenantCompany({ skipCacheHydrate: true });
        if (!cancelled) setLoading(false);

        if (!cancelled) {
          refreshCoreAppData().catch((err) => {
            if (!cancelled) {
              setError(err.message || 'Failed to load app data');
            }
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load app data');
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, hydrateTenantFromCache, refreshTenantCompany, refreshCoreAppData]);

  const value = useMemo(
    () => ({
      companies,
      companiesStats,
      watchlist,
      watchlistStats,
      watchlistLoading,
      userCompanies,
      supplierTierCounts,
      userCompaniesLoaded,
      dashboardBootstrap,
      tenantProfile,
      tenantNodesByKey,
      tenantSuppliers,
      tenantCompanyRoutes,
      tenantLoading,
      tenantDetailsLoading,
      tenantLoaded,
      tenantMapReady,
      patchTenantNodesByKey,
      setTenantProfile,
      loading,
      error,
      refreshCompanies,
      refreshDashboardBootstrap,
      refreshWatchlist,
      refreshUserCompanies,
      refreshTenantCompany,
      refreshAll,
    }),
    [
      companies,
      companiesStats,
      watchlist,
      watchlistStats,
      watchlistLoading,
      userCompanies,
      supplierTierCounts,
      userCompaniesLoaded,
      dashboardBootstrap,
      tenantProfile,
      tenantNodesByKey,
      tenantSuppliers,
      tenantCompanyRoutes,
      tenantLoading,
      tenantDetailsLoading,
      tenantLoaded,
      tenantMapReady,
      patchTenantNodesByKey,
      setTenantProfile,
      loading,
      error,
      refreshCompanies,
      refreshDashboardBootstrap,
      refreshWatchlist,
      refreshUserCompanies,
      refreshTenantCompany,
      refreshAll,
    ]
  );

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};
